import { forwardRef, useImperativeHandle, useRef, type KeyboardEvent, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { useFileDrop } from "./use-file-drop";

// Canonical file-drop field (@adr SPARK-0022-file-drop-fields@4). Two variants share ONE drag affordance:
//   - "overlay": wraps existing content (e.g. a folder tile). No resting frame.
//   - "zone": a standalone dashed area with a resting prompt; renders its own
//     contents (file list) via children, or the standard prompt when empty.
// While a file is dragged over EITHER variant, the content blurs behind a black
// dashed frame + a "drop to upload" message — identical everywhere. The active
// frame/blur/message and Esc-reset all come from one place so the four upload
// fields behave and read the same.
// A hidden <input> is owned here; openPicker() is exposed via ref for external
// "+ add" buttons, and clickToOpen wires a click (and keyboard) to the picker.

export interface FileDropZoneHandle {
  openPicker: () => void;
}

interface FileDropZoneProps {
  /** Kennung des versteckten Datei-Feldes — Tests legen darüber Dateien ab. */
  inputId?: string;
  variant?: "overlay" | "zone";
  /** Kennung des Ablagefeldes — nur `variant="zone"` rendert einen eigenen Rahmen dafür. */
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  onFiles: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Clicking the field opens the picker (consumer-gated, e.g. only when empty). */
  clickToOpen?: boolean;
  className?: string;
  /** The message shown on the blur overlay while dragging (defaults to a generic one). */
  dropLabel?: ReactNode;
  /** zone, empty state: the resting prompt line. */
  label?: ReactNode;
  /** zone, empty state: sub-hint under the prompt (formats, size limit). */
  hint?: ReactNode;
  children?: ReactNode;
}

export const FileDropZone = forwardRef<FileDropZoneHandle, FileDropZoneProps>(
  function FileDropZone(
    {
      inputId,
      variant = "overlay",
      id,
      ariaLabel,
      ariaDescribedBy,
      onFiles,
      accept,
      multiple = true,
      disabled = false,
      clickToOpen = false,
      className = "",
      dropLabel,
      label,
      hint,
      children,
    },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    const { dragOver, dropProps } = useFileDrop({ onFiles, disabled });
    useImperativeHandle(ref, () => ({
      openPicker: () => { if (!disabled) inputRef.current?.click(); },
    }), [disabled]);

    const onClick = !disabled && clickToOpen ? () => inputRef.current?.click() : undefined;
    // Keep the click-to-open affordance keyboard-accessible (a plain div with
    // onClick is not focusable/activatable on its own).
    const a11y = onClick
      ? {
          role: "button" as const,
          tabIndex: 0,
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          },
        }
      : {};

    const input = (
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={(e) => {
          if (!disabled && e.target.files?.length) onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    );

    // Shared drag affordance: blur the content behind a black dashed frame
    // (matches the box corners via rounded-[inherit]) + the drop message.
    const dropOverlay = dragOver ? (
      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-ink bg-page/40 px-3 text-center backdrop-blur-sm">
        <span className="font-serif text-smycles-body text-ink">
          {dropLabel ?? (
            <FormattedMessage id="common.fileDrop.overlay" defaultMessage="Drop to upload …" />
          )}
        </span>
      </div>
    ) : null;

    if (variant === "zone") {
      return (
        <div
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-disabled={disabled}
          {...dropProps}
          {...a11y}
          onClick={onClick}
          // Resting dashed frame; on drag it yields to the overlay's frame.
          className={`relative rounded-md border-2 border-dashed transition ${
            dragOver ? "border-transparent" : "border-ink/15 hover:border-accent-decoration/40"
          } ${onClick ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui" : ""} ${className}`}
        >
          {children ?? (
            <div className="flex flex-col items-center justify-center gap-1 py-6 text-center text-smycles-meta text-ink-muted">
              <span>
                {label ?? (
                  <FormattedMessage
                    id="common.fileDrop.zone"
                    defaultMessage="Drag a file here or click to upload"
                  />
                )}
              </span>
              {hint ? <span className="text-smycles-micro text-ink-muted">{hint}</span> : null}
            </div>
          )}
          {dropOverlay}
          {input}
        </div>
      );
    }

    return (
      <div
        id={id}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-disabled={disabled}
        {...dropProps}
        {...a11y}
        onClick={onClick}
        className={`relative transition ${onClick ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui" : ""} ${className}`}
      >
        {children}
        {dropOverlay}
        {input}
      </div>
    );
  },
);
